import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Image, Platform, ActivityIndicator, useWindowDimensions } from 'react-native';
import TopGreenHeader from '../Common/TopGreenHeader';
import { CalendarIcon } from '../../components/EventIcons';
import { useNavigation } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
import API_PATHS from '../../apiCalls/ApiPath';

const MOBILE_BREAKPOINT = 620;

const GREEN = '#6FA22E';
const INK = '#171720';
const MUTED = '#8B8B96';
const HAIRLINE = '#EBEBF2';
const CHIP_BG = '#EEF0FC';

const STATUS_COLORS = {
  ongoing: '#B7410E',
  upcoming: GREEN,
  completed: '#556B2F',
};

const cardShadow = Platform.select({
  web: { boxShadow: '0 1px 2px rgba(23,23,32,0.04), 0 8px 24px rgba(23,23,32,0.05)' },
  default: {
    shadowColor: '#0F1226',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.06,
    shadowRadius: 14,
    elevation: 3,
  },
});

const greenShadow = Platform.select({
  web: { boxShadow: '0 4px 12px rgba(111, 162, 46, 0.35)' },
  default: {
    shadowColor: GREEN,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.35,
    shadowRadius: 8,
    elevation: 4,
  },
});

const badgeShadow = Platform.select({
  web: { boxShadow: '0 2px 8px rgba(23,23,32,0.25)' },
  default: {
    shadowColor: '#0F1226',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 6,
    elevation: 3,
  },
});

const formatDate = (epochSeconds) => {
  const date = new Date(epochSeconds * 1000);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${month}/${day}/${year}`;
};

const getSeatSummary = (tickets) => {
  if (!tickets || tickets.length === 0) return null;
  const totalSeats = tickets.reduce((sum, t) => sum + (Number(t.totalSeats) || 0), 0);
  const remainingSeats = tickets.reduce((sum, t) => sum + (Number(t.remainingSeats) || 0), 0);
  return { totalSeats, remainingSeats };
};

const EventDatesList = ({ dates, isMobile }) => {
  const [rowWidths, setRowWidths] = useState({});

  if (!dates || dates.length === 0) return null;
  return (
    <View style={styles.datesBlock}>
      <Text style={styles.datesHeaderText}>Event Dates</Text>
      {dates.map((d, index) => {
        const key = d.eventDateId ?? index;
        const seats = getSeatSummary(d.tickets);
        return (
          <React.Fragment key={key}>
            <View
              style={[styles.dateEntry, isMobile && styles.dateEntryMobile]}
              onLayout={({ nativeEvent }) => {
                const width = nativeEvent.layout.width;
                setRowWidths((prev) => (prev[key] === width ? prev : { ...prev, [key]: width }));
              }}
            >
              <View style={styles.dateLeft}>
                <CalendarIcon size={14} color={GREEN} strokeWidth={2} />
                <Text style={[styles.dateLineText, isMobile && styles.dateLineTextMobile]}>
                  {formatDate(d.eventDate)} · {d.startTime} - {d.endTime}
                </Text>
              </View>
              {seats && (
                <View style={[styles.seatsPill, isMobile && styles.seatsPillMobile]}>
                  <Text style={[styles.seatsPillText, isMobile && styles.seatsPillTextMobile]}>
                    {seats.remainingSeats}/{seats.totalSeats} Seats Available
                  </Text>
                </View>
              )}
            </View>
            {index < dates.length - 1 && (
              <View
                style={[
                  styles.dateSeparator,
                  isMobile ? styles.dateSeparatorMobile : rowWidths[key] ? { width: rowWidths[key] } : null,
                ]}
              />
            )}
          </React.Fragment>
        );
      })}
    </View>
  );
};

const ProfileEventsList = () => {
  const navigation = useNavigation();
  const { width } = useWindowDimensions();
  const isMobile = width < MOBILE_BREAKPOINT;
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    (async () => {
      const storedUserId = await AsyncStorage.getItem('userId');
      if (storedUserId) setUserId(storedUserId);
    })();
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const response = await axios.post(`${API_PATHS.BASE_URL}${API_PATHS.GET_ALL_EVENTS}`, {
        limit: 10,
        page,
      });

      if (response.data.Success === 1) {
        const { events: newEvents, pagination } = response.data.Data;
        setEvents((prev) => (page === 1 ? newEvents : [...prev, ...newEvents]));
        setHasMore(pagination ? pagination.page < pagination.totalPages : newEvents.length === 10);
      } else if (page === 1) {
        setEvents([]);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setHasMore(false);
      if (page === 1) {
        setEvents([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [page]);

  useEffect(() => {
    fetchEvents();
  }, [page, fetchEvents]);

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) setPage((p) => p + 1);
  };

  const handleEventPress = (event) => {
    navigation.navigate('ProfileEventDetails', { event, userId });
  };

  return (
    <View style={styles.container}>
      <TopGreenHeader Heading="Camps" navigation={navigation} isBackIcon={true} isNotification={true} />

      <ScrollView
        contentContainerStyle={styles.scrollContent}
        onScroll={({ nativeEvent }) => {
          const { layoutMeasurement, contentOffset, contentSize } = nativeEvent;
          const isCloseToBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height - 20;
          if (isCloseToBottom) handleLoadMore();
        }}
        scrollEventThrottle={400}
      >
        <View style={[styles.listInner, isMobile && styles.listInnerMobile]}>
          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={GREEN} />
            </View>
          ) : events.length === 0 ? (
            <Text style={styles.emptyText}>No events available right now.</Text>
          ) : (
            <>
              {events.map((event) =>
                isMobile ? (
                  <TouchableOpacity
                    key={event.eventId}
                    activeOpacity={0.85}
                    style={[styles.mobileCardShadowWrap, cardShadow]}
                    onPress={() => handleEventPress(event)}
                  >
                    <View style={styles.mobileCard}>
                      <View style={styles.mobileImageWrapper}>
                        <Image
                          source={
                            event.pic_url?.[0] ? { uri: event.pic_url[0] } : require('../../assets/event-banner.png')
                          }
                          style={styles.mobileImage}
                          resizeMode="cover"
                        />
                        <View
                          style={[
                            styles.statusBadge,
                            styles.statusBadgeFloating,
                            badgeShadow,
                            { backgroundColor: STATUS_COLORS[event.status] || GREEN },
                          ]}
                        >
                          <Text style={styles.statusText}>{(event.status || '').toUpperCase()}</Text>
                        </View>
                      </View>
                      <View style={styles.mobileBody}>
                        <Text style={styles.eventTitle} numberOfLines={2}>
                          {event.title}
                        </Text>
                        <EventDatesList dates={event.dates} isMobile />
                      </View>
                    </View>
                  </TouchableOpacity>
                ) : (
                  <TouchableOpacity
                    key={event.eventId}
                    activeOpacity={0.8}
                    style={[styles.eventCard, cardShadow]}
                    onPress={() => handleEventPress(event)}
                  >
                    <View style={[styles.eventImageWrapper, greenShadow]}>
                      <Image
                        source={
                          event.pic_url?.[0] ? { uri: event.pic_url[0] } : require('../../assets/event-banner.png')
                        }
                        style={styles.eventImage}
                        resizeMode="cover"
                      />
                    </View>
                    <View style={styles.eventCardBody}>
                      <Text style={styles.eventTitle}>{event.title}</Text>
                      <EventDatesList dates={event.dates} />
                    </View>
                    <View
                      style={[
                        styles.statusBadge,
                        styles.statusBadgeDesktop,
                        { backgroundColor: STATUS_COLORS[event.status] || GREEN },
                      ]}
                    >
                      <Text style={[styles.statusText, styles.statusTextDesktop]}>
                        {(event.status || '').toUpperCase()}
                      </Text>
                    </View>
                  </TouchableOpacity>
                )
              )}
              {loadingMore && (
                <View style={styles.footerLoader}>
                  <ActivityIndicator size="small" color={GREEN} />
                </View>
              )}
            </>
          )}
        </View>
      </ScrollView>
    </View>
  );
};

const FONT = Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans';
const FONT_BOLD = Platform.OS === 'android' ? 'FacebookSansBold' : 'Facebook Sans';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  scrollContent: {
    backgroundColor: '#F8F8FC',
    flexGrow: 1,
  },
  listInner: {
    width: '100%',
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 20,
  },
  listInnerMobile: {
    paddingHorizontal: 14,
    paddingTop: 14,
  },
  eventCard: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: HAIRLINE,
    borderRadius: 16,
    paddingVertical: 18,
    paddingHorizontal: 20,
    marginBottom: 14,
  },
  eventImageWrapper: {
    alignSelf: 'center',
    width: 104,
    height: 104,
    borderRadius: 14,
    marginRight: 16,
  },
  eventImage: {
    width: '100%',
    height: '100%',
    borderRadius: 12,
    backgroundColor: CHIP_BG,
  },
  eventCardBody: {
    flex: 1,
    marginRight: 12,
  },
  mobileCardShadowWrap: {
    borderRadius: 18,
    marginBottom: 16,
  },
  mobileCard: {
    borderRadius: 18,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: HAIRLINE,
    backgroundColor: '#fff',
  },
  mobileImageWrapper: {
    width: '100%',
    height: 200,
    backgroundColor: CHIP_BG,
  },
  mobileImage: {
    width: '100%',
    height: '100%',
  },
  statusBadgeFloating: {
    position: 'absolute',
    top: 12,
    right: 12,
  },
  mobileBody: {
    padding: 16,
  },
  eventTitle: {
    fontSize: 18,
    fontFamily: FONT_BOLD,
    color: INK,
  },
  statusBadge: {
    backgroundColor: GREEN,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
  },
  statusBadgeDesktop: {
    paddingHorizontal: 13,
    paddingVertical: 5.5,
  },
  statusText: {
    color: '#fff',
    fontSize: 10.5,
    letterSpacing: 0.5,
    fontFamily: FONT_BOLD,
  },
  statusTextDesktop: {
    fontSize: 11.5,
  },
  datesBlock: {
    marginTop: 10,
  },
  datesHeaderText: {
    fontSize: 11,
    letterSpacing: 0.6,
    color: '#E94D8B',
    fontFamily: FONT_BOLD,
    marginBottom: 6,
  },
  dateEntry: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 8,
  },
  dateEntryMobile: {
    alignSelf: 'stretch',
    flexWrap: 'wrap',
    rowGap: 6,
    paddingVertical: 7,
  },
  dateSeparator: {
    alignSelf: 'flex-start',
    height: 1,
    backgroundColor: '#F2F2F7',
  },
  dateSeparatorMobile: {
    alignSelf: 'stretch',
    width: undefined,
  },
  dateLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  dateLineText: {
    fontSize: 13.5,
    color: MUTED,
    fontFamily: FONT,
  },
  dateLineTextMobile: {
    fontSize: 12.5,
  },
  seatsPill: {
    flexShrink: 0,
    backgroundColor: CHIP_BG,
    paddingHorizontal: 9,
    paddingVertical: 3,
    borderRadius: 999,
  },
  seatsPillMobile: {
    marginLeft: 22,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  seatsPillTextMobile: {
    fontSize: 11,
  },
  seatsPillText: {
    fontSize: 11,
    color: GREEN,
    fontFamily: FONT_BOLD,
    letterSpacing: 0.2,
  },
  loadingContainer: {
    paddingVertical: 40,
    alignItems: 'center',
  },
  footerLoader: {
    paddingVertical: 16,
    alignItems: 'center',
  },
  emptyText: {
    textAlign: 'center',
    color: MUTED,
    fontSize: 15,
    fontFamily: FONT,
    marginTop: 40,
  },
});

export default ProfileEventsList;
